import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, PackagePlus, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { BarcodeScanner } from "./BarcodeScanner";
import { IngredientEditor, field, label } from "./IngredientEditor";
import { fmtQty, invApi } from "./api";

/** Scan → known: quick quantity; unknown: register form. */
export function IntakeView({ token, onStockChanged }) {
  const [found, setFound] = useState(null);   // recognised ingredient
  const [newCode, setNewCode] = useState(""); // unrecognised barcode
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [pickList, setPickList] = useState([]);
  const [pick, setPick] = useState("");

  useEffect(() => {
    invApi(token).get("/inventory/ingredients").then((r) => setPickList(r.data)).catch(() => {});
  }, [token, recent.length]);

  const lookup = useCallback(async (code) => {
    try {
      const { data } = await invApi(token).get(`/inventory/ingredients/by-barcode/${encodeURIComponent(code)}`);
      setFound(data);
      setNewCode("");
      setQty("");
    } catch (err) {
      if (err?.response?.status === 404) {
        setFound(null);
        setNewCode(code);
      } else toast.error("Lookup failed — check connection");
    }
  }, [token]);

  const receive = async (e) => {
    e?.preventDefault?.();
    const q = parseFloat(qty);
    if (!found || !(q > 0)) return toast.error("Enter the quantity received");
    setBusy(true);
    try {
      const { data } = await invApi(token).post("/inventory/intake", { ingredient_id: found.id, quantity: q, note });
      toast.success(`+${fmtQty(q)} ${data.unit} ${data.name} · now ${fmtQty(data.current_stock)}`);
      setRecent((r) => [{ id: Date.now(), name: data.name, qty: q, unit: data.unit, stock: data.current_stock }, ...r].slice(0, 8));
      setFound(null);
      setQty("");
      setNote("");
      onStockChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't record intake");
    } finally {
      setBusy(false);
    }
  };

  const paused = !!found || !!newCode;

  return (
    <div className="space-y-4" data-testid="intake-view">
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-2">
          <ScanLine className="w-4 h-4 text-brand-red" />
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest">Scan intake</div>
          <div className="ml-auto text-[11px] text-stone-500">Scan a delivery to add stock</div>
        </div>
        <div className="p-4 space-y-3">
          <BarcodeScanner onScan={lookup} paused={paused} />
          <div className="flex items-center gap-2">
            <select value={pick} onChange={(e) => setPick(e.target.value)} className={field} data-testid="intake-pick-select">
              <option value="">No barcode? Pick an ingredient…</option>
              {pickList.map((i) => <option key={i.id} value={i.id}>{i.name} ({fmtQty(i.current_stock)} {i.unit})</option>)}
            </select>
            <button type="button" disabled={!pick} data-testid="intake-pick-btn"
              onClick={() => { const i = pickList.find((x) => x.id === pick); if (i) { setFound(i); setNewCode(""); setPick(""); } }}
              className="shrink-0 bg-stone-800 text-white px-4 py-2.5 rounded-xl text-xs uppercase tracking-widest font-black disabled:opacity-40">Select</button>
          </div>
        </div>
      </div>

      {found && (
        <form onSubmit={receive} className="bg-white border-2 border-emerald-300 rounded-2xl p-5 space-y-3" data-testid="intake-found-card">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-black text-stone-900 text-lg leading-tight" data-testid="intake-found-name">{found.name}</div>
              <div className="text-xs text-stone-500">
                {found.barcode ? <span className="font-mono">{found.barcode} · </span> : null}
                On hand: <b data-testid="intake-found-stock">{fmtQty(found.current_stock)} {found.unit}</b>
                {found.low_stock && <span className="ml-2 text-red-600 font-bold">LOW</span>}
              </div>
            </div>
          </div>
          <label className="block"><span className={label}>Quantity received ({found.unit})</span>
            <input value={qty} onChange={(e) => setQty(e.target.value)} type="number" step="any" min="0" inputMode="decimal" autoFocus data-testid="intake-qty-input"
              className="w-full px-4 py-3 rounded-2xl border-2 border-stone-200 bg-white text-2xl font-black tabular-nums text-center outline-none focus:border-emerald-500" /></label>
          <div className="grid grid-cols-4 gap-2">
            {[1, 6, 12, 24].map((n) => (
              <button key={n} type="button" onClick={() => setQty(String((parseFloat(qty) || 0) + n))} data-testid={`intake-quick-${n}`}
                className="py-2 rounded-xl bg-stone-100 text-stone-800 font-black text-sm active:scale-95">+{n}</button>
            ))}
          </div>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (vendor, invoice #…) — optional" className={field} data-testid="intake-note-input" />
          <div className="flex gap-2">
            <button type="button" onClick={() => setFound(null)} data-testid="intake-cancel-btn"
              className="flex-1 py-3 rounded-full border-2 border-stone-200 text-xs uppercase tracking-widest font-black text-stone-600">Cancel</button>
            <button type="submit" disabled={busy} data-testid="intake-submit-btn"
              className="flex-[2] py-3 rounded-full bg-emerald-600 text-white text-xs uppercase tracking-widest font-black disabled:opacity-50 flex items-center justify-center gap-2">
              <PackagePlus className="w-4 h-4" /> {busy ? "Saving…" : "Add to stock"}
            </button>
          </div>
        </form>
      )}

      {newCode && (
        <IngredientEditor token={token} initial={{ barcode: newCode }} onClose={() => setNewCode("")}
          onSaved={(ing) => { setNewCode(""); setRecent((r) => [{ id: Date.now(), name: ing.name, qty: ing.current_stock, unit: ing.unit, stock: ing.current_stock, isNew: true }, ...r].slice(0, 8)); onStockChanged?.(); }} />
      )}

      {recent.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="intake-recent">
          <div className="px-5 py-3 border-b border-stone-200 font-black text-stone-800 text-sm uppercase tracking-widest">This session</div>
          <ul className="divide-y divide-stone-100">
            {recent.map((r) => (
              <li key={r.id} className="px-5 py-2.5 flex items-center justify-between text-sm">
                <span className="font-bold text-stone-800">{r.name}{r.isNew && <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-700 font-black">new</span>}</span>
                <span className="text-emerald-700 font-black tabular-nums">+{fmtQty(r.qty)} {r.unit} <span className="text-stone-400 font-bold text-xs">→ {fmtQty(r.stock)}</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
